<script setup>
/**
 * 낭만작명 구매 내역 한 줄 카드.
 * Figma 디자인(310:28128) 기준.
 *
 * 1행: [작명 기준 사주 이름 Bold] + [선택 정보 회색 작은 글씨]
 *   - sajuName: 구매 당시 선택한 작명 기준 사주의 이름 (예: '이몽룡')
 *   - selectionInfo: 모드 + 입력 식별값 (namingPurchaseLabel.formatNamingPurchaseLabel 결과)
 * 2행: 구매 날짜 / 유효기간(주황)
 * 우측: chevron (상세 펼침 트리거)
 */
defineProps({
  /** 1행 메인 — 작명 기준 사주 이름 */
  sajuName: { type: String, default: '' },
  /** 1행 서브 — 모드 + 식별값 회색 텍스트 (예: '한글이름추천 — 김씨 (남자 · 2글자)') */
  selectionInfo: { type: String, default: '' },
  /** 구매 날짜 문자열 (예: '2026.01.02') */
  purchaseDate: { type: String, default: '' },
  /** 유효기간 만료 날짜 문자열 (예: '2026.04.02') */
  validUntil: { type: String, default: '' },
  /** 활성(현재 보고 있는/방금 구매한) 항목 여부 — 강조 표시 */
  active: { type: Boolean, default: false },
});

const emit = defineEmits(['click']);
</script>

<template>
  <button
    type="button"
    class="purchase-history-card"
    :class="{ 'purchase-history-card--active': active }"
    @click="emit('click')"
  >
    <div class="purchase-history-card__body">
      <div class="purchase-history-card__title-row">
        <span class="purchase-history-card__saju-name">{{ sajuName }}</span>
        <span v-if="selectionInfo" class="purchase-history-card__selection">{{ selectionInfo }}</span>
      </div>
      <div class="purchase-history-card__meta">
        <span class="purchase-history-card__date">{{ purchaseDate }}</span>
        <span v-if="validUntil" class="purchase-history-card__valid">유효기간 {{ validUntil }}</span>
      </div>
    </div>
    <span class="purchase-history-card__chevron" aria-hidden="true">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.5 5L12.5 10L7.5 15" stroke="#aaa" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </span>
  </button>
</template>

<style lang="scss" scoped>
/* 낭만궁합 .harmony-sheet__card와 동일 톤 — border 전체 + 둥근 모서리, 시트 내부에서 리스트로 쌓임 */
.purchase-history-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  border: 1px solid #ebebeb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  transition: border-color 0.2s ease;
  font-family: inherit;

  &:hover {
    border-color: #ff8800;
  }

  &--active {
    border-color: #ff8800;
    background: #fff8f5;
  }
}

.purchase-history-card__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.purchase-history-card__title-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  width: 100%;
  min-width: 0;
}

.purchase-history-card__saju-name {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  flex-shrink: 0;
  white-space: nowrap;
}

.purchase-history-card__selection {
  flex: 1;
  min-width: 0;
  color: #999;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.purchase-history-card__meta {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.purchase-history-card__date {
  color: #333;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
}

.purchase-history-card__valid {
  color: #eb4c10;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.purchase-history-card__chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
</style>
